/**
 * Country → identity scheme map.
 * BR: CPF declaration + Brazilian CNH (front/back) + selfie.
 * US: Driver's License (front/back) + selfie (no CPF).
 * Unknown countries: generic national-id image + selfie (or passport path).
 */

export const NATIONAL_ID_SCHEMES = {
  BR: {
    id: "scheme_br",
    countryCode: "BR",
    nationalIdName: "CPF",
    proofKind: "drivers_license",
    proofCountry: "BR",
    requiresNationalId: false,
    suggestsNationalId: true,
    requiresSelfie: true,
    proofSlots: [
      { type: "DRIVERS_LICENSE", variant: "cpfProof", proofCountryCode: "BR" },
      { type: "DRIVERS_LICENSE_BACK", variant: "cpfProof", proofCountryCode: "BR" },
      { type: "SELFIE" },
    ],
    locale: {
      proofTitleKey: "baas.entidades.detail.nationalIdProofTitle",
      proofHintKey: "baas.entidades.detail.nationalIdProofHint",
      pathHintKey: "baas.entidades.detail.documentsNationalIdPathHint",
      uploadGroupKey: "baas.entidades.detail.documentsUploadNationalIdProof",
    },
  },
  US: {
    id: "scheme_us",
    countryCode: "US",
    nationalIdName: null,
    proofKind: "drivers_license_standalone",
    proofCountry: "US",
    requiresNationalId: false,
    requiresSelfie: true,
    proofSlots: [
      { type: "DRIVERS_LICENSE", variant: "standalone", proofCountryCode: "US" },
      { type: "DRIVERS_LICENSE_BACK", variant: "standalone", proofCountryCode: "US" },
      { type: "SELFIE" },
    ],
    locale: {
      proofTitleKey: "baas.entidades.detail.driversLicenseProofTitleUs",
      proofHintKey: "baas.entidades.detail.driversLicenseProofHintUs",
      pathHintKey: "baas.entidades.detail.documentsDriversLicensePathHint",
      uploadGroupKey: "baas.entidades.detail.documentsUploadDriversLicenseUs",
    },
  },
};

export const GENERIC_NATIONAL_ID_SCHEME = {
  id: "generic",
  countryCode: null,
  nationalIdName: null,
  proofKind: "national_id_image",
  proofCountry: null,
  requiresNationalId: false,
  requiresSelfie: true,
  proofSlots: [
    {
      type: "NATIONAL_ID",
      isDeclarationImage: true,
    },
    { type: "SELFIE" },
  ],
  locale: {
    proofTitleKey: "baas.entidades.detail.nationalIdProofTitleGeneric",
    proofHintKey: "baas.entidades.detail.nationalIdProofHintGeneric",
    pathHintKey: "baas.entidades.detail.documentsNationalIdPathHintGeneric",
    uploadGroupKey: "baas.entidades.detail.documentsUploadNationalIdProofGeneric",
  },
};

export const normalizeSchemeCountry = (code) => String(code || "").trim().toUpperCase();

export const resolveNationalIdScheme = (countryCode) => {
  const cc = normalizeSchemeCountry(countryCode);
  return NATIONAL_ID_SCHEMES[cc] || GENERIC_NATIONAL_ID_SCHEME;
};

export const nationalIdNumberIsFilled = (nationalId) =>
  Boolean(String(nationalId?.number || nationalId?.Number || "").trim());

/** When National ID / CPF is declared, passport is a separate path — hide it. */
export const shouldHidePassportForNationalId = (nationalId) =>
  nationalIdNumberIsFilled(nationalId);

export const getNationalIdProofSlots = (countryCode) => {
  const cc = normalizeSchemeCountry(countryCode);
  if (!cc) return [];
  return resolveNationalIdScheme(cc).proofSlots;
};

/** Proof image slots for passport declarations (always includes selfie). */
export const PROOF_SLOTS_BY_DECLARATION_TYPE = {
  PASSPORT: [
    { type: "PASSPORT", isDeclarationImage: true },
    { type: "SELFIE" },
  ],
  REGISTRATION_ID: [
    { type: "REGISTRATION_ID", isDeclarationImage: true },
    { type: "SELFIE" },
  ],
};

/** UI-only variants when the API type alone is ambiguous (e.g. CPF vs RG → NATIONAL_ID). */
export const IDENTITY_VARIANT = {
  CPF: "cpf",
  RG: "rg",
  ITIN: "itin",
  CRM: "crm",
};

export const getDeclarationPickerId = (type, identityVariant = null) => {
  const docType = String(type || "").toUpperCase();
  const variant = String(identityVariant || "").trim().toLowerCase();
  if (
    variant
    && (docType === "NATIONAL_ID" || (docType === "REGISTRATION_ID" && variant === IDENTITY_VARIANT.CRM))
  ) {
    return `${docType}:${variant.toUpperCase()}`;
  }
  return docType;
};

const BR_STANDALONE_CNH_PROOF_SLOTS = [
  { type: "DRIVERS_LICENSE", variant: "standalone", proofCountryCode: "BR" },
  { type: "DRIVERS_LICENSE_BACK", variant: "standalone", proofCountryCode: "BR" },
  { type: "SELFIE" },
];

/**
 * Proof slots bundled with a declared document (doc images + selfie in same save).
 * Selfie is never a standalone declaration type — only a proof inside the block.
 */
export const getDeclarationProofSlots = (countryCode, documentType, options = {}) => {
  const cc = normalizeSchemeCountry(countryCode);
  const type = String(documentType || "").toUpperCase();
  const identityVariant = String(options.identityVariant || "").trim().toLowerCase();
  if (!cc || !type) return [];

  if (type === "SSN" || type === "PROOF_OF_RESIDENCE") return [];

  if (type === "NATIONAL_ID") {
    if (identityVariant === IDENTITY_VARIANT.ITIN) return [];
    if (identityVariant === IDENTITY_VARIANT.RG) {
      return GENERIC_NATIONAL_ID_SCHEME.proofSlots;
    }
    if (identityVariant === IDENTITY_VARIANT.CPF || cc === "BR") {
      // CPF number only — CNH/selfie are a separate DRIVERS_LICENSE declaration.
      return [];
    }
    return GENERIC_NATIONAL_ID_SCHEME.proofSlots;
  }

  if (type === "PASSPORT") return PROOF_SLOTS_BY_DECLARATION_TYPE.PASSPORT || [];

  if (type === "REGISTRATION_ID") {
    return PROOF_SLOTS_BY_DECLARATION_TYPE.REGISTRATION_ID || [];
  }

  if (type === "DRIVERS_LICENSE") {
    if (cc === "BR") return BR_STANDALONE_CNH_PROOF_SLOTS;
    const scheme = resolveNationalIdScheme(cc);
    if (
      scheme.proofKind === "drivers_license_standalone"
      || scheme.proofKind === "drivers_license"
    ) {
      return scheme.proofSlots;
    }
    return [
      { type: "DRIVERS_LICENSE", variant: "standalone", proofCountryCode: cc },
      { type: "DRIVERS_LICENSE_BACK", variant: "standalone", proofCountryCode: cc },
      { type: "SELFIE" },
    ];
  }

  return [];
};

/** Primary declaration types shown in country → type picker (no proof-only types). */
const US_TAX_ID_TYPES = [
  { value: "SSN", group: "taxId", required: false },
  { value: "NATIONAL_ID", identityVariant: IDENTITY_VARIANT.ITIN, group: "taxId", required: false },
];

const PERSON_DECLARATION_PICKER_TYPES = {
  BR: [
    { value: "DRIVERS_LICENSE", group: "nationalId", required: false },
    { value: "NATIONAL_ID", identityVariant: IDENTITY_VARIANT.CPF, group: "nationalId", required: false },
    { value: "NATIONAL_ID", identityVariant: IDENTITY_VARIANT.RG, group: "nationalId", required: false },
    { value: "PASSPORT", group: "passport", required: false },
    { value: "REGISTRATION_ID", identityVariant: IDENTITY_VARIANT.CRM, group: "professional", required: false },
    { value: "PROOF_OF_RESIDENCE", group: "other", required: false },
  ],
  US: [
    ...US_TAX_ID_TYPES,
    { value: "DRIVERS_LICENSE", group: "nationalId", required: false },
    { value: "PASSPORT", group: "passport", required: false },
    { value: "PROOF_OF_RESIDENCE", group: "other", required: false },
  ],
  DEFAULT: [
    { value: "DRIVERS_LICENSE", group: "nationalId", required: false },
    { value: "PASSPORT", group: "passport", required: false },
    { value: "NATIONAL_ID", group: "nationalId", required: false },
    { value: "PROOF_OF_RESIDENCE", group: "other", required: false },
  ],
};

/**
 * Person declaration types unlocked after the operator picks an issuing country.
 * Selfie and DL back are proof slots only — never standalone picker entries.
 */
export const getPersonUploadTypesForCountry = (countryCode) => {
  const cc = normalizeSchemeCountry(countryCode);
  if (!cc) return [];

  if (cc === "BR") return PERSON_DECLARATION_PICKER_TYPES.BR;
  if (cc === "US") return PERSON_DECLARATION_PICKER_TYPES.US;
  return PERSON_DECLARATION_PICKER_TYPES.DEFAULT;
};

/** Hard requirement (none today — CPF is suggested for BR Conta Digital, not blocking). */
export const countryRequiresCpf = (countryCode) =>
  resolveNationalIdScheme(countryCode).requiresNationalId === true;

/** Soft hint: BR Conta Digital path still benefits from CPF, but foreign/US identity is allowed. */
export const countrySuggestsCpf = (countryCode) =>
  resolveNationalIdScheme(countryCode).suggestsNationalId === true;
